import { findLexicalSets, lexicalSets } from "../rules/findLexicalSets";
import { replaceLexicalTokens } from "../rules/replaceLexicalSets";
import { RulesRunner } from "../rules/rulesRunner";
import { americanistToIpa } from "../ruleSets/americanist";
import { oldEnglishRules } from "../ruleSets/oldEnglish";
import { cxsToIpa } from "../ruleSets/cxs";
import { englishToIpa } from "../ruleSets/englishToIpa";
import { lushootseedToIpa } from "../ruleSets/lushootseed";
import { SampleRules } from "../ruleSets/sampleRules";
import { sanskritTransliteration } from "../ruleSets/sanskrit";
import { spanishToIpa } from "../ruleSets/spanishToIpa";
import { wordsToIpa } from "../transcribe/wordsToIpa";
import { AllRules } from "../ruleSets/allRules";
import { PhoneLengths } from "../rules/lengthRules";

export type InputLanguageOptions = 'US' | 'UK' | 'US rules' | 'Lexical Sets' | 'Old English' | 'Spanish' | 'Lushootseed'
  | 'Americanist' | 'Sanskrit (IAST)' | 'CXS';

/**
 * There are 3 different inputs/outputs: language, ipa, & afterRules.
 * 'language' -> 'ipa' either thru a dictionary (like US) or thru special raw->ipa rules (like Old English).
 * 'ipa' -> 'afterRules' happens thru any of a set of named rules.
 */
export class PhonemeShifter {
  private languageType: InputLanguageOptions = 'US';
  private usEnglishRules = new RulesRunner(englishToIpa);
  private oldEnglishRules = new RulesRunner(oldEnglishRules);
  private spanishRules = new RulesRunner(spanishToIpa);
  private lushootseedRules = new RulesRunner(lushootseedToIpa);
  private americanistRules = new RulesRunner(americanistToIpa);
  private sanskritRules = new RulesRunner(sanskritTransliteration);
  private cxsRules = new RulesRunner(cxsToIpa);
  private runners = new SampleRules();

  getRuleNames(): string[] {
    return this.runners.getNames();
  }

  setLanguage(language: InputLanguageOptions): void {
    this.languageType = language;
  }

  transformLanguage(
    language: string, ruleRef: string | RulesRunner
  ): { ipa: string, afterRules: string, badLanguage?: string[], badIpa?: string[] } {
    if (this.useDictionary()) {
      const [ipa, badLanguage] = wordsToIpa(language, this.languageType === 'UK' ? 'en_UK' : 'en_US', this.fallback);
      const { afterRules, badIpa } = this.runRule(language, ruleRef);
      return { ipa, afterRules, badLanguage: AllRules.deDupe(badLanguage), badIpa };
    } else if (this.languageType === 'Lexical Sets') {
      const tokenized = findLexicalSets(language);
      const lookup = lexicalSets.map(set => '(' + set.name + ')');
      const ipa = replaceLexicalTokens(tokenized, lookup);

      const [, badLanguage] = wordsToIpa(language, 'en_US', this.fallback);
      const { afterRules, badIpa } = this.runRule(language, ruleRef);
      return { ipa, afterRules, badLanguage, badIpa };
    } else {
      const rules = this.getRules();
      const results = rules.runRulesOnIpa(language);
      const badLanguage = !results.hasUnknowns ? [] : results.hasUnknowns.map(p => p.phoneme);
      if (results.ipa) {
        const ipa = results.ipa;
        const { afterRules, badIpa } = this.runRule(ipa, ruleRef);
        return { ipa, afterRules, badLanguage, badIpa };
      }
      return { ipa: '', afterRules: '', badLanguage };
    }
  }

  applyRule(
    input: { language: string, ipa: string }, ruleRef: string | RulesRunner
  ): { afterRules: string, badIpa?: string[] } {
    return this.runRule(this.useDictionary() || this.languageType === 'Lexical Sets' ? input.language : input.ipa, ruleRef);
  }

  runRuleOnIpa(
    input: { ipa: string }, ruleRef: string | RulesRunner
  ): { afterRules: string, badIpa?: string[] } {
    const runner = typeof ruleRef === 'string' ? this.runners.runners[ruleRef] : ruleRef;
    const result = runner.runRulesOnIpa(input.ipa);
    const badIpa = !result.hasUnknowns ? [] : result.hasUnknowns.map(p => p.phoneme);
    return result.ipa ? { afterRules: result.ipa, badIpa } : { afterRules: '', badIpa };
  }

  /** get lengthener for named rule (like 'Australian') or the current language (like en or es) */
  getAddPhonemeLength(ruleRef: string | true): PhoneLengths | undefined {
    const runner = ruleRef === true ? this.getRules()
      : this.runners.runners[ruleRef] ?? this.getRules();
    return runner === undefined ? runner : runner.getAddPhonemeLength();
  }

  private runRule(
    input: string, ruleRef: string | RulesRunner
  ): { afterRules: string, badIpa?: string[] } {
    if (ruleRef === '') {
      return { afterRules: '' };
    }
    const runner = typeof ruleRef === 'string' ? this.runners.runners[ruleRef] : ruleRef;
    if (this.useDictionary() || this.languageType === 'Lexical Sets') {
      const [ipa, badIpa] = runner.runRulesOnRaw(input, this.fallback);
      // could highlight notFound after a short pause
      return { afterRules: ipa, badIpa };
    } else {
      const result = runner.runRulesOnIpa(input);
      const badIpa = !result.hasUnknowns ? [] : result.hasUnknowns.map(p => p.phoneme);
      if (result.ipa) {
        return { afterRules: result.ipa, badIpa };
      }
    }
    return { afterRules: '' };
  }

  private useDictionary(): boolean {
    return this.languageType === 'US' || this.languageType === 'UK';
  }

  private fallback = (word: string): string => {
    const result = this.usEnglishRules.runRulesOnIpa(word);
    return result.ipa;
  }

  private getRules(): RulesRunner {
    switch (this.languageType) {
      case 'US rules': return this.usEnglishRules;
      case 'Old English': return this.oldEnglishRules;
      case 'Spanish': return this.spanishRules;
      case 'Lushootseed': return this.lushootseedRules;
      case 'Americanist': return this.americanistRules;
      case 'Sanskrit (IAST)': return this.sanskritRules;
      case 'CXS': return this.cxsRules;
    }
    return this.usEnglishRules;
  }
}
